import {createResource, createResourceFromFile, getFile, getResource, Resource, updateResource} from "./common"
import {Account, isAccountResource, isCreditAccount} from "./account"
import {Cents} from "./transaction"

export type Source = "DebitCard" | "ACH"

export function getSourceDisplay(dispute: Dispute, include?: Resource[]): string {
	if (dispute.attributes.source === "ACH") return "ACH"
	const accountId = dispute.relationships.account?.data?.id
	if (!accountId || !include) return "Debit Card"
	const account = include.find((r): r is Account => r.id === accountId && isAccountResource(r))
	return account && isCreditAccount(account) ? "Credit Card" : "Debit Card"
}

export enum StatusType {
	InvestigationStarted = "Investigation Started",
	ProvisionallyCredited = "Provisionally Credited",
	Denied = "Denied",
	ResolvedLost = "Resolved Lost",
	ResolvedWon = "Resolved Won",
}

export enum DisputeType {
	Issuer = "Issuer",
	Fraud = "Fraud",
	NonFraud = "NonFraud",
}

export type Status = {
	type: StatusType
	updatedAt: Date
	updatedBy?: string
}

export type BussinessDayDTO = {
	type: "bussinessDayDTO"
	attributes: {
		date: string
		text: string
	}
}

export type DeferredProvisionalCreditDTO = {
	type: "deferredProvisionalCreditWillBeGranted"
	id: string
	attributes: {
		provisionalCreditDate: string
	}
}
export const deferredProvisionalCreditType: DeferredProvisionalCreditDTO["type"] =
	"deferredProvisionalCreditWillBeGranted"

export type DeferredResolveLost = {
	type: "deferredResolveLost"
	id: string
	attributes: {
		deferredDate: string
	}
}
export const deferredResolveLostType: DeferredResolveLost["type"] = "deferredResolveLost"

export type NoDeferredBehaviorDTO = {
	type: "deferredProvisionalCreditNotGranted"
}
export const noDeferredBehaviorType: NoDeferredBehaviorDTO["type"] = "deferredProvisionalCreditNotGranted"

export type ActiveDeferredBehaviorDTO = DeferredProvisionalCreditDTO | DeferredResolveLost

export type DeferredBehaviorDTO = ActiveDeferredBehaviorDTO | NoDeferredBehaviorDTO

export type Dispute = {
	type: "dispute"
	id: string
	attributes: {
		source: Source
		externalId: string
		link: string
		description: string
		disputeType: DisputeType
		statusHistory: Status[]
		status: StatusType
		createdAt: Date
		updatedAt?: Date
		amount: Cents
		decisionReason?: string
		deferredProvisionalCredit?: DeferredBehaviorDTO
		possibleBussinesDays: BussinessDayDTO[]
	}
	relationships: {
		customer: {
			data: {
				type: "customer"
				id: string
			}
		}
		account: {
			data: {
				type: "account"
				id: string
			}
		}
		transaction: {
			data: {
				type: "transaction"
				id: string
			}
		}
		merchantCreditTransactionId?: {
			data: {
				type: "merchantCreditTransactionId"
				id: string
			}
		}
		org?: {
			data: {
				type: "org"
				id: string
			}
		}
	}
}

export type uploadBulkDisputesResult = {
	type: string
	attributes: {
		newDisputes: number
		resolvedWon: number
		resolvedLost: number
		denied: number
		noDecision: number
		noActionNeeded: number
	}
}

type Include = "customer,org" | "customer,org,account" | "" | "org" | undefined

export function getDeferredBehaviorAttribute(dispute: Dispute) {
	return dispute.attributes.deferredProvisionalCredit
}

export function hasActiveDeferredBehaviorDTOWithFutureDate(dispute: Dispute) {
	if (getDeferredBehaviorAttribute(dispute)?.type !== noDeferredBehaviorType) {
		const dueDate = getActiveDeferredBehaviorDueDateAttribute(dispute)
		return dueDate !== undefined && !isTodayAfterDate(dueDate)
	}
	return false
}

export function getActiveDeferredBehaviorDisplayName(dispute: Dispute) {
	const deferredBehavior = getDeferredBehaviorAttribute(dispute) as ActiveDeferredBehaviorDTO
	if (deferredBehavior?.type === deferredProvisionalCreditType) {
		return "Provisional Credit"
	}
	if (deferredBehavior?.type === deferredResolveLostType) {
		return "Resolve Lost"
	}
	return ""
}

export function getActiveDeferredBehaviorDueDateAttribute(dispute: Dispute): string | undefined {
	const deferredBehavior = getDeferredBehaviorAttribute(dispute) as ActiveDeferredBehaviorDTO
	if (deferredBehavior?.type === deferredProvisionalCreditType) {
		return deferredBehavior.attributes.provisionalCreditDate
	}
	if (deferredBehavior?.type === deferredResolveLostType) {
		return deferredBehavior.attributes.deferredDate
	}
}
export function setActiveDeferredBehaviorDueDateAttribute(
	dispute: Dispute,
	deferredDate: string
): ActiveDeferredBehaviorDTO {
	const deferredBehavior = getDeferredBehaviorAttribute(dispute) as ActiveDeferredBehaviorDTO
	if (deferredBehavior?.type === deferredProvisionalCreditType) {
		deferredBehavior.attributes.provisionalCreditDate = deferredDate
	}
	if (deferredBehavior?.type === deferredResolveLostType) {
		deferredBehavior.attributes.deferredDate = deferredDate
	}
	return deferredBehavior
}

function isTodayAfterDate(dateToCheck: string): boolean {
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const date = new Date(dateToCheck)

	return today > date
}

export function shouldShowDeferredBehavior(dispute: Dispute) {
	return (
		dispute.attributes.status.toString() === "InvestigationStarted" &&
		hasActiveDeferredBehaviorDTOWithFutureDate(dispute)
	)
}

export function StatusTypeValue(key: string): string {
	return StatusType[key as keyof typeof StatusType]
}

export async function getDispute(accessToken: string, id: string, extraFields?: Record<"dispute", string>) {
	const query = {
		extraFields: extraFields,
		include: "org,account",
	}

	return await getResource<Dispute>(`disputes/${id}`, accessToken, query)
}

export async function findDisputes(
	accessToken: string,
	offset: number,
	requestType: "json" | "csv",
	limit?: number | null,
	customerId?: string,
	statuses?: string[],
	transactionId?: number | string,
	orgs?: string[],
	searchQuery?: string,
	include?: Include,
	cardId?: string
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : undefined
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			customerId,
			cardId,
			statuses,
			transactionId,
			orgs: orgFilter,
			query: searchQuery != "" ? searchQuery : null,
		},
		include: include,
	}

	switch (requestType) {
		case "csv":
			return await getFile("disputes", accessToken, query)
		default:
			return await getResource<Array<Dispute>>("disputes", accessToken, query)
	}
}

export async function createDispute(
	accessToken: string,
	externalId: string,
	link: string | null,
	description: string | null,
	disputeType: string,
	accountId: string,
	transactionId: string,
	amount: number,
	autoGrantProvisionalCredit: boolean
) {
	const result = await createResource<Dispute>("disputes", accessToken, {
		type: "dispute",
		attributes: {
			externalId,
			link,
			description,
			amount,
			disputeType,
			...(autoGrantProvisionalCredit && {
				deferredProvisionalCredit: {
					type: "automaticallyGrantDeferredProvisionalCredit",
				},
			}),
		},
		relationships: {
			account: {
				data: {
					type: "account",
					id: accountId,
				},
			},
			transaction: {
				data: {
					type: "transaction",
					id: transactionId,
				},
			},
		},
	})
	return result.map((v) => v.data)
}

export async function uploadBulkDispute(accessToken: string, inputFile: any, type: string, fileName: string) {
	const result = await createResourceFromFile<uploadBulkDisputesResult>(
		"disputes/bulk",
		accessToken,
		inputFile,
		type,
		fileName
	)
	return result.map((v) => v.data)
}

export async function setDisputeAction(accessToken: string, id: string, action: string, decisionReason?: string) {
	const result = await createResource<Dispute>(
		`disputes/${id}/${action}`,
		accessToken,
		decisionReason ? {decisionReason} : null
	)
	return result.map((v) => v.data)
}

export async function updateDispute(
	accessToken: string,
	id: string,
	description: string,
	disputeType: DisputeType,
	amount: number | null,
	externalId?: string
) {
	const result = await updateResource<Dispute>(`disputes/${id}`, accessToken, {
		type: "dispute",
		attributes: {
			description,
			amount,
			disputeType,
			externalId,
		},
	})
	return result.map((v) => v.data)
}

export async function cancelDeferredBehavior(accessToken: string, id: string) {
	const result = await updateResource<Dispute>(`disputes/${id}`, accessToken, {
		type: "dispute",
		attributes: {
			deferredProvisionalCredit: {
				type: noDeferredBehaviorType,
			},
		},
	})
	return result.map((v) => v.data)
}

export async function changeDeferredBehaviorDueDate(accessToken: string, dispute: Dispute, date: string) {
	const newDeferredBehavior = setActiveDeferredBehaviorDueDateAttribute(dispute, date)
	const result = await updateResource<Dispute>(`disputes/${dispute.id}`, accessToken, {
		type: "dispute",
		attributes: {
			deferredProvisionalCredit: newDeferredBehavior,
		},
	})
	return result.map((v) => v.data)
}
